import React, { useEffect, useState } from "react";
import { useI18n, formatPrice } from "../i18n";
import { api, resolveAsset } from "../api";
import { Link } from "react-router-dom";
import { Radio, CheckCircle2, Clock, ArrowRight } from "lucide-react";

const LiveFleet = () => {
  const { lang } = useI18n();
  const isPt = lang === "pt";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get("/live/fleet");
      const fleet = (res.data.fleet || []).map((c) => ({
        ...c,
        image: resolveAsset(c.image),
      }));
      setData({ ...res.data, fleet });
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000); // refresh every 30s
    return () => clearInterval(t);
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20" data-testid="live-fleet-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-label text-[#FFB400]">
            <span className="line-accent" />
            <Radio size={11} className="inline mr-1 -mt-0.5 animate-pulse" />
            {isPt ? "Frota ao vivo" : "Live fleet"}
          </p>
          <h1 className="mt-4 font-heading text-5xl sm:text-6xl font-bold tracking-tighter text-white max-w-3xl">
            {isPt
              ? "Veja o pátio em tempo real."
              : "See the lot in real time."}
          </h1>
          <p className="mt-6 max-w-2xl text-zinc-300 text-lg leading-relaxed">
            {isPt
              ? "Sem letra miúda, sem call center. Os carros disponíveis agora, os que voltam hoje e as próximas reservas — tudo público."
              : "No fine print, no call center. The cars available now, the ones coming back today and the next bookings — all public."}
          </p>
        </div>
        {data?.summary && (
          <div className="bg-[#0d0d0d] border border-white/10 p-6 min-w-[260px]" data-testid="live-summary">
            <p className="text-label">{isPt ? "Resumo agora" : "Right now"}</p>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <div>
                <p className="font-heading text-3xl font-bold text-[#FFB400]">{data.summary.available}</p>
                <p className="text-xs text-zinc-500 font-mono-ui uppercase tracking-widest">{isPt ? "Disponíveis" : "Available"}</p>
              </div>
              <div>
                <p className="font-heading text-3xl font-bold text-white">{data.summary.out}</p>
                <p className="text-xs text-zinc-500 font-mono-ui uppercase tracking-widest">{isPt ? "Em rota" : "Out"}</p>
              </div>
              <div>
                <p className="font-heading text-3xl font-bold text-white">{data.summary.total}</p>
                <p className="text-xs text-zinc-500 font-mono-ui uppercase tracking-widest">{isPt ? "Total" : "Total"}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="live-grid">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="aspect-[4/3] bg-[#0d0d0d] border border-white/5 animate-pulse" />
          ))
        ) : (
          data?.fleet.map((c) => (
            <div
              key={c.id}
              className="bg-[#0d0d0d] border border-white/10 overflow-hidden lift"
              data-testid={`live-car-${c.id}`}
            >
              <div className="relative aspect-[16/10] bg-[#0a0a0a]">
                <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
                <div className="absolute top-3 left-3">
                  {c.status === "available" ? (
                    <span className="bg-[#00d96b] text-[#0a0a0a] text-[10px] font-mono-ui px-2 py-1 tracking-widest uppercase">
                      ● {isPt ? "Disponível" : "Available"}
                    </span>
                  ) : (
                    <span className="bg-[#FFB400] text-[#0a0a0a] text-[10px] font-mono-ui px-2 py-1 tracking-widest uppercase">
                      ● {isPt ? "Em rota" : "Out"}
                    </span>
                  )}
                </div>
              </div>
              <div className="p-5">
                <p className="text-label">{c.brand}</p>
                <h3 className="font-heading text-xl text-white tracking-tight">{c.name}</h3>

                <div className="mt-4 space-y-2 text-sm">
                  {c.status === "out" ? (
                    <p className="flex items-center gap-2 text-zinc-300">
                      <Clock size={14} className="text-[#FFB400]" />
                      {isPt ? "Volta em" : "Returning"} <strong>{c.returning_on}</strong>
                    </p>
                  ) : c.next_pickup_on ? (
                    <p className="flex items-center gap-2 text-zinc-300">
                      <CheckCircle2 size={14} className="text-[#00d96b]" />
                      {isPt ? "Próx. retirada" : "Next pickup"} <strong>{c.next_pickup_on}</strong>
                    </p>
                  ) : (
                    <p className="flex items-center gap-2 text-zinc-400">
                      <CheckCircle2 size={14} className="text-[#00d96b]" />
                      {isPt ? "Sem reservas — pegue agora!" : "No bookings — yours now!"}
                    </p>
                  )}
                  <p className="text-zinc-500 text-xs">
                    {c.total_bookings_ahead} {isPt ? "reserva(s) próximas" : "upcoming booking(s)"} ·{" "}
                    {formatPrice(c.price_per_day)}/{isPt ? "dia" : "day"}
                  </p>
                </div>

                <Link
                  to={`/booking/${c.id}`}
                  className="mt-5 btn-primary w-full justify-center text-sm py-2.5"
                  data-testid={`live-book-${c.id}`}
                >
                  {isPt ? "Reservar" : "Reserve"} <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          ))
        )}
      </div>

      {data?.as_of && (
        <p className="mt-10 text-xs text-zinc-600 font-mono-ui uppercase tracking-widest text-center">
          {isPt ? "Atualizado em" : "As of"} {new Date(data.as_of).toLocaleString()}
        </p>
      )}
    </div>
  );
};

export default LiveFleet;
